import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import DesignModeToggle from "../components/terminal/DesignModeToggle";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LogIn, UserPlus } from "lucide-react";

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Honor `?next=<path>` deep-link redirects (set by protected-page guards
  // like /account/betslips).  Falls back to /account.
  const nextPath = (() => {
    const raw = searchParams.get("next") || "";
    // Defensive: only allow same-origin relative paths.
    return raw.startsWith("/") && !raw.startsWith("//") ? raw : "/account";
  })();

  const submit = async (e) => {
    e.preventDefault();
    // Login accepts any non-empty password (server validates the lockout
    // counter); register applies the new ≥10-char policy.
    const minLen = mode === "register" ? 10 : 1;
    if (!email || password.length < minLen) {
      toast.error(mode === "register" ? t("auth.passwordMin") : "Enter your password");
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success(t("auth.welcomeToast"));
      } else {
        await register(email, password);
        toast.success(t("auth.accountCreatedToast"));
      }
      navigate(nextPath);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || t("auth.authFailed");
      toast.error(typeof msg === "string" ? msg : t("auth.authFailed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
      {/* Floating design-mode toggle so users can switch to 2.0 from auth too */}
      <div className="fixed top-4 right-4 z-50">
        <DesignModeToggle />
      </div>
      <div
        className="w-full max-w-md rounded-lg p-10 nd-fade-up"
        style={{ background: "var(--v3-surface)", border: "1px solid var(--v3-line)" }}
      >
        <Link to="/" className="inline-flex items-center gap-2.5 mb-10" data-testid="auth-logo-link">
          <img
            src="/ndakubet-logo.jpg"
            alt="NDAKUBET"
            className="h-9 w-9 rounded-md object-cover"
          />
          <div
            className="text-[16px]"
            style={{ fontWeight: 600, letterSpacing: "-0.025em" }}
          >
            Ndakubet
          </div>
        </Link>

        <div
          className="text-[11px] uppercase inline-flex items-center gap-2 mb-3"
          style={{
            color: "var(--v3-text-3)",
            letterSpacing: "0.14em",
            fontFamily: "var(--v3-font-mono)",
          }}
        >
          <span className="h-1 w-1 rounded-full" style={{ background: "var(--v3-indigo)" }} />
          {mode === "login" ? "Sign in" : "Create account"}
        </div>
        <h1
          className="text-3xl md:text-4xl mb-2"
          style={{ fontWeight: 500, letterSpacing: "-0.04em", lineHeight: 1.05 }}
        >
          {mode === "login" ? t("auth.welcomeBack") : t("auth.createAnAccount")}
        </h1>
        <p
          className="text-[14px] mb-8"
          style={{ color: "var(--v3-text-2)" }}
        >
          {mode === "login" ? t("auth.subtitleLogin") : t("auth.subtitleRegister")}
        </p>

        <div
          className="inline-flex items-stretch gap-6 mb-8 w-full"
          data-testid="auth-tabs"
          style={{ borderBottom: "1px solid var(--v3-line)" }}
        >
          <button
            type="button"
            onClick={() => setMode("login")}
            data-testid="auth-tab-login"
            className="relative pb-3 pt-0 text-[13px] transition"
            style={{
              color: mode === "login" ? "var(--v3-text)" : "var(--v3-text-3)",
              fontWeight: mode === "login" ? 500 : 400,
            }}
          >
            {t("common.login")}
            {mode === "login" && (
              <span className="absolute left-0 right-0 -bottom-px h-px" style={{ background: "var(--v3-text)" }} />
            )}
          </button>
          <button
            type="button"
            onClick={() => setMode("register")}
            data-testid="auth-tab-register"
            className="relative pb-3 pt-0 text-[13px] transition"
            style={{
              color: mode === "register" ? "var(--v3-text)" : "var(--v3-text-3)",
              fontWeight: mode === "register" ? 500 : 400,
            }}
          >
            {t("common.register")}
            {mode === "register" && (
              <span className="absolute left-0 right-0 -bottom-px h-px" style={{ background: "var(--v3-text)" }} />
            )}
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
          <div>
            <Label
              htmlFor="email"
              className="text-[11px] uppercase"
              style={{
                color: "var(--v3-text-3)",
                letterSpacing: "0.12em",
                fontFamily: "var(--v3-font-mono)",
              }}
            >
              {t("auth.email")}
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="auth-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
              className="mt-2 h-11 rounded-md"
              style={{ background: "var(--v3-canvas)", border: "1px solid var(--v3-line)" }}
            />
          </div>
          <div>
            <Label
              htmlFor="password"
              className="text-[11px] uppercase"
              style={{
                color: "var(--v3-text-3)",
                letterSpacing: "0.12em",
                fontFamily: "var(--v3-font-mono)",
              }}
            >
              {t("auth.password")}
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="auth-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "register" ? "min. 10 chars · letter + digit" : "your password"}
              required
              className="mt-2 h-11 rounded-md"
              style={{ background: "var(--v3-canvas)", border: "1px solid var(--v3-line)" }}
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="auth-submit"
            className="w-full h-11 rounded-md text-[14px]"
            style={{
              background: "var(--v3-indigo)",
              color: "var(--v3-text)",
              fontWeight: 500,
              letterSpacing: "-0.005em",
            }}
          >
            {mode === "login" ? <LogIn className="w-4 h-4 mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
            {busy ? t("common.loading") : mode === "login" ? t("auth.loginCta") : t("auth.registerCta")}
          </Button>
        </form>

        <p className="text-center text-[12px] mt-8" style={{ color: "var(--v3-text-3)" }}>
          <Link
            to="/"
            className="underline underline-offset-4 hover:text-foreground"
            data-testid="auth-back-home"
          >
            {t("auth.backToPredictions")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
